import { errorMsg } from '../../utils/errorMsg';
import React, { useState, useEffect, useCallback } from 'react';
import {
  Container, Typography, Box, Button, Chip, CircularProgress, Alert,
  Tabs, Tab, Grid, Card, CardContent, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, InputLabel, Select, MenuItem, TextField
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoIcon from '@mui/icons-material/Info';
import PeopleIcon from '@mui/icons-material/People';
import { useNavigate, useParams } from 'react-router-dom';
import {
  getGC, listarMembrosGC, adicionarMembroGC, adicionarVisitanteGC, removerMembroGC, aprovarGC
} from '../../api/gruposCrescimento';
import api from '../../axios';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import ContextNavBar from '../../components/ContextNavBar';
import ModuleHeader from '../../components/ModuleHeader';

const DIAS_SEMANA = {
  domingo: 'Domingo', segunda: 'Segunda-feira', terca: 'Terça-feira',
  quarta: 'Quarta-feira', quinta: 'Quinta-feira', sexta: 'Sexta-feira', sabado: 'Sábado'
};

function TabPanel({ value, index, children }) {
  return value === index ? <Box sx={{ pt: 3 }}>{children}</Box> : null;
}

function GCDetalhe() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [gc, setGc] = useState(null);
  const [membros, setMembros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');
  const [tab, setTab] = useState(0);

  const [openAddMembro, setOpenAddMembro] = useState(false);
  const [membrosDisponiveis, setMembrosDisponiveis] = useState([]);
  const [membroSelecionado, setMembroSelecionado] = useState('');
  const [addTipo, setAddTipo] = useState('membro');
  const [visitanteNome, setVisitanteNome] = useState('');
  const [visitanteContato, setVisitanteContato] = useState('');
  const [confirmRemover, setConfirmRemover] = useState(null);
  const [aprovando, setAprovando] = useState(false);

  const carregarGC = useCallback(async () => {
    try {
      const data = await getGC(id);
      setGc(data);
    } catch (err) {
      setErro(errorMsg(err) || 'Erro ao carregar GC');
    }
  }, [id]);

  const carregarMembros = useCallback(async () => {
    try {
      const data = await listarMembrosGC(id);
      setMembros(data);
    } catch {
      setMembros([]);
    }
  }, [id]);

  useEffect(() => {
    setLoading(true);
    Promise.all([carregarGC(), carregarMembros()])
      .finally(() => setLoading(false));
  }, [carregarGC, carregarMembros]);

  const abrirAdicionarMembro = async () => {
    setMembroSelecionado('');
    setVisitanteNome('');
    setVisitanteContato('');
    setAddTipo('membro');
    try {
      const res = await api.get('/secretaria/membros');
      setMembrosDisponiveis(res.data);
      setOpenAddMembro(true);
    } catch {
      alert('Erro ao carregar membros');
    }
  };

  const adicionarMembro = async () => {
    try {
      if (addTipo === 'membro') {
        if (!membroSelecionado) return;
        await adicionarMembroGC(id, parseInt(membroSelecionado, 10));
      } else {
        if (!visitanteNome.trim()) return;
        await adicionarVisitanteGC(id, visitanteNome.trim(), visitanteContato.trim());
      }
      setOpenAddMembro(false);
      setMembroSelecionado('');
      setVisitanteNome('');
      setVisitanteContato('');
      carregarMembros();
    } catch (err) {
      alert(errorMsg(err) || 'Erro ao adicionar');
    }
  };

  const removerMembro = async () => {
    if (!confirmRemover) return;
    try {
      await removerMembroGC(id, confirmRemover.id);
      setConfirmRemover(null);
      carregarMembros();
    } catch (err) {
      alert(errorMsg(err) || 'Erro ao remover membro');
      setConfirmRemover(null);
    }
  };

  const handleAprovar = async () => {
    setAprovando(true);
    try {
      await aprovarGC(id);
      carregarGC();
    } catch (err) {
      alert(errorMsg(err) || 'Erro ao aprovar GC');
    }
    setAprovando(false);
  };

  if (loading) {
    return (
      <>
        <Header />
        <ContextNavBar context="grupos_crescimento" />
        <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 }, px: { xs: 1, md: 2 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
        </Container>
        <Footer />
      </>
    );
  }

  if (erro) {
    return (
      <>
        <Header />
        <ContextNavBar context="grupos_crescimento" />
        <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 }, px: { xs: 1, md: 2 } }}>
          <Alert severity="error">{erro}</Alert>
        </Container>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <ContextNavBar context="grupos_crescimento" />
      <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 }, px: { xs: 1, md: 2 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/grupos-crescimento')} size="small">Voltar</Button>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1, flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 1, md: 0 } }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, fontSize: { xs: '1.2rem', md: '1.5rem' } }}>{gc.nome}</Typography>
          <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
            <Chip label={gc.status || 'ativo'} color={gc.status === 'inativo' ? 'default' : 'success'} size="small" />
            <Chip label={gc.aprovado ? 'Aprovado' : 'Pendente'} color={gc.aprovado ? 'success' : 'warning'} size="small" />
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {!gc.aprovado && (
            <Button
              variant="contained"
              color="success"
              startIcon={<CheckCircleIcon />}
              onClick={handleAprovar}
              disabled={aprovando}
            >
              {aprovando ? <CircularProgress size={18} /> : 'Aprovar GC'}
            </Button>
          )}
          <Button variant="outlined" startIcon={<EditIcon />} onClick={() => navigate(`/grupos-crescimento/${id}/editar`)}>
            Editar
          </Button>
        </Box>
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', mt: 2 }}>
        <Tab icon={<InfoIcon />} iconPosition="start" label="Visão Geral" />
        <Tab icon={<PeopleIcon />} iconPosition="start" label="Membros" />
      </Tabs>

      <TabPanel value={tab} index={0}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Card elevation={1} sx={{ borderRadius: 2 }}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Informações do GC</Typography>
                <Grid container spacing={2}>
                  {gc.descricao && (
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="text.secondary">Descrição</Typography>
                      <Typography variant="body2">{gc.descricao}</Typography>
                    </Grid>
                  )}
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">Líder</Typography>
                    <Typography variant="body2">{gc.lider_nome || '—'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">Co-líder</Typography>
                    <Typography variant="body2">{gc.co_lider_nome || '—'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">Dia da Semana</Typography>
                    <Typography variant="body2">{DIAS_SEMANA[gc.dia_semana] || gc.dia_semana || '—'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">Horário</Typography>
                    <Typography variant="body2">{gc.horario || '—'}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">Endereço</Typography>
                    <Typography variant="body2">{gc.endereco || '—'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">Bairro</Typography>
                    <Typography variant="body2">{gc.bairro || '—'}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">Anfitriões</Typography>
                    <Box sx={{ mt: 0.5 }}>
                      {gc.anfitriao_nome1 && (
                        <Typography variant="body2">{gc.anfitriao_nome1}{gc.anfitriao_contato1 ? ` — ${gc.anfitriao_contato1}` : ''}</Typography>
                      )}
                      {gc.anfitriao_nome2 && (
                        <Typography variant="body2">{gc.anfitriao_nome2}{gc.anfitriao_contato2 ? ` — ${gc.anfitriao_contato2}` : ''}</Typography>
                      )}
                      {!gc.anfitriao_nome1 && !gc.anfitriao_nome2 && (
                        <Typography variant="body2" color="text.secondary">Nenhum anfitrião cadastrado</Typography>
                      )}
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">Total de Membros</Typography>
                    <Typography variant="body2">{membros.length}</Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={tab} index={1}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button variant="contained" startIcon={<PersonAddIcon />} onClick={abrirAdicionarMembro}>
            Adicionar Membro
          </Button>
        </Box>

        <TableContainer component={Paper} elevation={1} sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Nome</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Tipo</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Telefone</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Data Entrada</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {membros.map((m) => (
                  <TableRow key={m.id} hover>
                  <TableCell sx={{ fontWeight: 600 }}>{m.nome || '—'}</TableCell>
                  <TableCell>
                    <Chip label={m.tipo === 'visitante' ? 'Visitante' : 'Membro'} size="small" color={m.tipo === 'visitante' ? 'warning' : 'primary'} variant="outlined" />
                  </TableCell>
                  <TableCell>{m.email || '—'}</TableCell>
                  <TableCell>{m.telefone || '—'}</TableCell>
                  <TableCell>
                    {m.data_entrada ? new Date(m.data_entrada).toLocaleDateString('pt-BR') : '—'}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Remover">
                      <IconButton size="small" color="error" onClick={() => setConfirmRemover(m)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {membros.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Alert severity="info" sx={{ my: 1 }}>Nenhum membro neste GC.</Alert>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      <Dialog open={openAddMembro} onClose={() => setOpenAddMembro(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Adicionar ao GC</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <Button size="small" variant={addTipo === 'membro' ? 'contained' : 'outlined'} onClick={() => setAddTipo('membro')}>Membro</Button>
            <Button size="small" variant={addTipo === 'visitante' ? 'contained' : 'outlined'} onClick={() => setAddTipo('visitante')}>Visitante</Button>
          </Box>
          {addTipo === 'membro' ? (
            <FormControl fullWidth>
              <InputLabel>Membro</InputLabel>
              <Select value={membroSelecionado} label="Membro" onChange={e => setMembroSelecionado(e.target.value)}>
                <MenuItem value="">Selecione</MenuItem>
                {membrosDisponiveis.map(m => (
                  <MenuItem key={m.id} value={m.id}>{m.nome}</MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField label="Nome do Visitante" value={visitanteNome} onChange={e => setVisitanteNome(e.target.value)} fullWidth />
              <TextField label="Contato (telefone/email)" value={visitanteContato} onChange={e => setVisitanteContato(e.target.value)} fullWidth />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAddMembro(false)}>Cancelar</Button>
          <Button onClick={adicionarMembro} variant="contained">Adicionar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!confirmRemover} onClose={() => setConfirmRemover(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Remover Membro</DialogTitle>
        <DialogContent>
          <Typography>Remover "{confirmRemover?.nome || confirmRemover?.membro_nome}" deste GC?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRemover(null)}>Cancelar</Button>
          <Button color="error" variant="contained" onClick={removerMembro}>Confirmar</Button>
        </DialogActions>
      </Dialog>
    </Container>
      <Footer />
    </>
  );
}

export default GCDetalhe;
